import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import {
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';

const TIMER_STORAGE_KEY = 'gym-assistant-timer-state';

export interface TimerState {
  startedAt: string; // ISO string
  isPaused: boolean;
  pausedAt: string | null; // ISO string
  totalPausedSeconds: number;
}

interface WorkoutTimerProps {
  startedAt: string; // ISO string from when workout started
  onTimerStateChange?: (state: TimerState) => void;
  initialState?: TimerState | null;
}

const formatTime = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const WorkoutTimer: React.FC<WorkoutTimerProps> = ({ startedAt, onTimerStateChange, initialState }) => {
  const [timerState, setTimerState] = useState<TimerState>(() => {
    // Try to restore from initialState or sessionStorage
    if (initialState) return initialState;

    try {
      const saved = sessionStorage.getItem(TIMER_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as TimerState;
        if (parsed.startedAt === startedAt) return parsed;
      }
    } catch (e) {
      // ignore
    }

    return {
      startedAt,
      isPaused: false,
      pausedAt: null,
      totalPausedSeconds: 0,
    };
  });

  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const calculateElapsed = useCallback((state: TimerState): number => {
    const now = Date.now();
    const start = new Date(state.startedAt).getTime();
    let totalElapsed = Math.floor((now - start) / 1000);

    if (state.isPaused && state.pausedAt) {
      // Don't count time since pause started
      const pausedSince = Math.floor((now - new Date(state.pausedAt).getTime()) / 1000);
      totalElapsed -= pausedSince;
    }

    totalElapsed -= state.totalPausedSeconds;
    return Math.max(0, totalElapsed);
  }, []);

  // Persist timer state
  useEffect(() => {
    sessionStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify(timerState));
    onTimerStateChange?.(timerState);
  }, [timerState, onTimerStateChange]);

  // Update elapsed time every second
  useEffect(() => {
    const tick = () => setElapsed(calculateElapsed(timerState));
    tick(); // immediate update

    if (!timerState.isPaused) {
      intervalRef.current = setInterval(tick, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [timerState, calculateElapsed]);

  const handlePause = () => {
    setTimerState(prev => ({
      ...prev,
      isPaused: true,
      pausedAt: new Date().toISOString(),
    }));
  };

  const handleResume = () => {
    setTimerState(prev => {
      const pausedDuration = prev.pausedAt
        ? Math.floor((Date.now() - new Date(prev.pausedAt).getTime()) / 1000)
        : 0;
      return {
        ...prev,
        isPaused: false,
        pausedAt: null,
        totalPausedSeconds: prev.totalPausedSeconds + pausedDuration,
      };
    });
  };

  const isPaused = timerState.isPaused;

  return (
    <Box
      data-testid="workout-timer"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Chip
        icon={<TimerIcon />}
        label={
          <Typography
            variant="body2"
            fontWeight="bold"
            component="span"
            data-testid="timer-display"
          >
            {formatTime(elapsed)}
          </Typography>
        }
        color={isPaused ? 'warning' : 'success'}
        variant="filled"
        size="small"
        sx={{
          '& .MuiChip-label': { px: 1 },
          animation: isPaused ? 'pulse 1.5s ease-in-out infinite' : 'none',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.5 },
          },
        }}
      />
      <IconButton
        onClick={isPaused ? handleResume : handlePause}
        size="small"
        color={isPaused ? 'success' : 'warning'}
        data-testid="timer-pause-resume"
        title={isPaused ? 'Продължи' : 'Пауза'}
      >
        {isPaused ? <PlayIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
      </IconButton>
    </Box>
  );
};

export { TIMER_STORAGE_KEY };
export default WorkoutTimer;
